import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Timer, Trash2 } from 'lucide-react';
import countries from 'i18n-iso-countries';
import enCountries from 'i18n-iso-countries/langs/en.json';
import Select from 'react-select';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type FreeThreshold = { minSubtotalUsd: number; maxWeightLbs: number; feeUsd: number };
type CountryRate = {
  borderFeeUsd: number;
  baseFeeUsd?: number;
  perMileUsd: number;
  perLbUsd: number;
  freeThreshold?: FreeThreshold;
};
type CountryRule = {
  countryCode: string;
  enabled?: boolean;
  standardDays?: number;
  expressDays?: number;
  standard: CountryRate;
  express: CountryRate & { enabled: boolean };
};
type PricingConfigV2 = {
  version: 2;
  storeCountry: string;
  expressEnabled: boolean;
  standardDays: number;
  expressDays: number;
  countries: CountryRule[];
};

type StoreConfig = { latitude: number; longitude: number; label?: string };
type DeliveryPricingGetResponse = {
  pricing: PricingConfigV2;
  store: StoreConfig | null;
  storeOverride: StoreConfig | null;
  storeSource: 'override' | 'public_contact' | 'none';
  publicContactAddress: string | null;
};

const inputClass =
  'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2';

const sectionCardClass =
  'rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-900/[0.04] sm:p-6';
const sectionTitleClass = 'text-base font-semibold tracking-tight text-slate-900 sm:text-lg';
const fieldLabelClass = 'mb-1.5 block text-sm font-medium text-slate-700';
const btnPrimaryClass =
  'inline-flex items-center justify-center rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-600/20 transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50';
const btnSecondaryClass =
  'inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';
const btnGhostClass =
  'inline-flex items-center justify-center rounded-xl border border-transparent px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900';

countries.registerLocale(enCountries as any);

function normalizeCountryCode(input: string): string {
  return String(input || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
}

type CountryOption = { value: string; label: string };

function countryNameForCode(code: string): string {
  const iso2 = normalizeCountryCode(code);
  if (!iso2) return '';
  const name = countries.getName(iso2, 'en', { select: 'official' }) as string | undefined;
  return name || iso2;
}

function clamp0(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function toNum(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function DeliveryPricingSettingsPageV2() {
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const isAdmin = auth.type === 'ADMIN';
  const perms = auth.permissions ?? [];
  const canEdit = isAdmin || perms.includes('DELIVERY_PRICING:UPDATE');
  const canEditPublicContact = isAdmin || perms.includes('UI_SETTING:UPDATE');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [pricing, setPricing] = useState<PricingConfigV2 | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [countrySearch, setCountrySearch] = useState('');
  const [expandedCountries, setExpandedCountries] = useState<Record<string, boolean>>({});
  const [storeEffective, setStoreEffective] = useState<StoreConfig | null>(null);
  const [storeSource, setStoreSource] = useState<DeliveryPricingGetResponse['storeSource']>('none');
  const [publicContactAddress, setPublicContactAddress] = useState<string | null>(null);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideLat, setOverrideLat] = useState('');
  const [overrideLng, setOverrideLng] = useState('');
  const [overrideLabel, setOverrideLabel] = useState('');
  /** Overview vs full editor — when false, only summary + CTA. */
  const [pricingEditorOpen, setPricingEditorOpen] = useState(false);
  /** Last persisted pricing JSON for dirty detection. */
  const [savedPricingJson, setSavedPricingJson] = useState('');

  const applyLoaded = useCallback((data: DeliveryPricingGetResponse) => {
    setPricing(data.pricing);
    setSavedPricingJson(JSON.stringify(data.pricing));
    setDraft({});
    setExpandedCountries({});
    setStoreEffective(data.store);
    setStoreSource(data.storeSource);
    setPublicContactAddress(data.publicContactAddress);
    if (data.storeOverride) {
      setOverrideLat(String(data.storeOverride.latitude));
      setOverrideLng(String(data.storeOverride.longitude));
      setOverrideLabel(data.storeOverride.label ?? '');
    } else if (data.store) {
      setOverrideLat(String(data.store.latitude));
      setOverrideLng(String(data.store.longitude));
      setOverrideLabel(data.store.label ?? '');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/delivery-pricing`);
      if (!res.ok) throw new Error('load');
      const data = (await res.json()) as DeliveryPricingGetResponse;
      applyLoaded(data);
    } catch {
      setError(t('backoffice.delivery_pricing_load_error'));
    } finally {
      setLoading(false);
    }
  }, [applyLoaded, globalAuthorizedFetch, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!pricing) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/delivery-pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing }),
      });
      if (!res.ok) throw new Error('save');
      const data = (await res.json()) as DeliveryPricingGetResponse;
      applyLoaded(data);
      setSaved(true);
    } catch {
      setError(t('backoffice.delivery_pricing_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const saveOverride = async () => {
    const lat = Number(overrideLat);
    const lng = Number(overrideLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setError(t('backoffice.delivery_pricing_save_error'));
      return;
    }
    setSavingOverride(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/delivery-pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store: {
            latitude: lat,
            longitude: lng,
            label: overrideLabel.trim() || undefined,
          },
        }),
      });
      if (!res.ok) throw new Error('override');
      const data = (await res.json()) as DeliveryPricingGetResponse;
      applyLoaded(data);
    } catch {
      setError(t('backoffice.delivery_pricing_save_error'));
    } finally {
      setSavingOverride(false);
    }
  };

  const clearOverride = async () => {
    const ok = window.confirm(t('backoffice.delivery_pricing_clear_override_confirm'));
    if (!ok) return;
    setSavingOverride(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/delivery-pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ store: null }),
      });
      if (!res.ok) throw new Error('clear');
      const data = (await res.json()) as DeliveryPricingGetResponse;
      applyLoaded(data);
      setShowOverride(false);
    } catch {
      setError(t('backoffice.delivery_pricing_save_error'));
    } finally {
      setSavingOverride(false);
    }
  };

  const sortedCountries = useMemo(() => {
    const list = pricing?.countries ?? [];
    return [...list].sort((a, b) => a.countryCode.localeCompare(b.countryCode));
  }, [pricing?.countries]);

  const countryOptions = useMemo(() => {
    const list = countries.getNames('en', { select: 'official' }) as Record<string, string>;
    const opts: CountryOption[] = Object.entries(list)
      .map(([code, name]) => ({ value: code, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return opts;
  }, []);

  const selectedStoreCountry = useMemo(() => ({ value: 'US', label: countryNameForCode('US') }), []);

  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q) return sortedCountries;
    return sortedCountries.filter((c) => {
      const code = normalizeCountryCode(c.countryCode);
      const name = countryNameForCode(code).toLowerCase();
      return code.toLowerCase().includes(q) || name.includes(q);
    });
  }, [countrySearch, sortedCountries]);

  const addCountry = () => {
    setPricing((prev) => {
      if (!prev) return prev;
      const existsUS = prev.countries.some((c) => normalizeCountryCode(c.countryCode) === 'US');
      const next = existsUS ? 'CA' : 'US';
      return {
        ...prev,
        countries: [
          ...prev.countries,
          {
            countryCode: next,
            enabled: true,
            standard: { borderFeeUsd: 0, baseFeeUsd: 0, perMileUsd: 0, perLbUsd: 0 },
            express: { borderFeeUsd: 0, baseFeeUsd: 0, perMileUsd: 0, perLbUsd: 0, enabled: true },
          },
        ],
      };
    });
  };

  const addCountryByCode = (codeRaw: string) => {
    const code = normalizeCountryCode(codeRaw);
    if (!code) return;
    setPricing((prev) => {
      if (!prev) return prev;
      if (prev.countries.some((c) => normalizeCountryCode(c.countryCode) === code)) return prev;
      return {
        ...prev,
        countries: [
          ...prev.countries,
          {
            countryCode: code,
            enabled: true,
            standard: { borderFeeUsd: 0, baseFeeUsd: 0, perMileUsd: 0, perLbUsd: 0 },
            express: { borderFeeUsd: 0, baseFeeUsd: 0, perMileUsd: 0, perLbUsd: 0, enabled: true },
          },
        ],
      };
    });
    setExpandedCountries({ [code]: true });
  };

  const setCountry = (countryCode: string, updater: (c: CountryRule) => CountryRule) => {
    setPricing((prev) => {
      if (!prev) return prev;
      return { ...prev, countries: prev.countries.map((c) => (c.countryCode === countryCode ? updater(c) : c)) };
    });
  };

  const removeCountry = (countryCode: string) => {
    setPricing((prev) => {
      if (!prev) return prev;
      if (normalizeCountryCode(countryCode) === 'US') return prev;
      if (normalizeCountryCode(countryCode) === normalizeCountryCode(prev.storeCountry)) return prev;
      return { ...prev, countries: prev.countries.filter((c) => c.countryCode !== countryCode) };
    });
  };

  /** Accordion: only one country’s rate editor open at a time. */
  const toggleExpanded = (countryCode: string) => {
    const code = normalizeCountryCode(countryCode);
    setExpandedCountries((s) => {
      const wasOpen = !!s[code];
      const cleared: Record<string, boolean> = {};
      if (!wasOpen) cleared[code] = true;
      return cleared;
    });
  };

  const isPricingDirty = useMemo(() => {
    if (!pricing || !savedPricingJson) return false;
    return JSON.stringify(pricing) !== savedPricingJson;
  }, [pricing, savedPricingJson]);

  const closePricingEditor = () => {
    if (isPricingDirty) {
      const ok = window.confirm(
        t(
          'backoffice.delivery_pricing_discard_confirm',
          'You have unsaved pricing changes. Discard them and return to the overview?',
        ),
      );
      if (!ok) return;
      try {
        setPricing(JSON.parse(savedPricingJson) as PricingConfigV2);
        setDraft({});
      } catch {
        void load();
      }
    }
    setPricingEditorOpen(false);
    setShowOverride(false);
    setExpandedCountries({});
    setCountrySearch('');
  };

  const sourceLabel =
    storeSource === 'override'
      ? t('backoffice.delivery_pricing_store_source_override')
      : storeSource === 'public_contact'
        ? t('backoffice.delivery_pricing_store_source_public')
        : t('backoffice.delivery_pricing_store_source_none');

  const setDraftValue = (key: string, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const clearDraftValue = (key: string) => {
    setDraft((d) => {
      if (!(key in d)) return d;
      const next = { ...d };
      delete next[key];
      return next;
    });
  };

  const valueFromDraft = (key: string, fallbackNumber: number) => {
    return draft[key] ?? String(fallbackNumber);
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/delivery-pricing">
      <main className="workspace-page">
        {!pricingEditorOpen ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-2 text-brand-600">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 ring-1 ring-brand-100">
                  <Timer className="h-5 w-5" strokeWidth={2} />
                </span>
                <h1 className="workspace-page-title">
                  {t('backoffice.delivery_pricing_title')}
                </h1>
              </div>
              <p className="max-w-3xl workspace-page-subtitle text-slate-600">{t('backoffice.delivery_pricing_subtitle')}</p>
            </div>
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-slate-600">{t('common.loading')}</p>
        ) : !pricing ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <>
            {!pricingEditorOpen ? (
              <div className="workspace-panel space-y-3">
                {error ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
                ) : null}
                {saved ? (
                  <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">
                    {t('backoffice.delivery_pricing_saved')}
                  </p>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className={`${sectionCardClass} border-t-4 border-t-brand-500`}>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {t('backoffice.delivery_pricing_store_section')}
                    </p>
                    <p className="mt-2 text-sm text-slate-700">
                      {storeEffective
                        ? `${storeEffective.latitude.toFixed(4)}, ${storeEffective.longitude.toFixed(4)}`
                        : '—'}
                    </p>
                    <p className="mt-2">
                      <span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                        {sourceLabel}
                      </span>
                    </p>
                    <p className="mt-3 text-xs text-slate-600">
                      {storeEffective?.label?.trim() || publicContactAddress?.trim() || '—'}
                    </p>
                  </div>
                  <div className={`${sectionCardClass} border-t-4 border-t-emerald-500`}>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {t('backoffice.delivery_pricing_rules_section')}
                    </p>
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {pricing.expressEnabled
                        ? t('backoffice.delivery_pricing_overview_express_on', 'Standard & express')
                        : t('backoffice.delivery_pricing_overview_express_off', 'Standard only')}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                      {t('backoffice.delivery_pricing_days_standard')}: {pricing.standardDays} ·{' '}
                      {t('backoffice.delivery_pricing_days_express')}: {pricing.expressDays}
                    </p>
                  </div>
                  <div className={`${sectionCardClass} border-t-4 border-t-amber-500`}>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                      {t('backoffice.delivery_pricing_countries_section')}
                    </p>
                    <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">{sortedCountries.length}</p>
                    <p className="text-sm text-slate-600">
                      {t('backoffice.delivery_pricing_overview_markets', 'countries / regions')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  {canEdit ? (
                    <button type="button" className={`${btnPrimaryClass} min-h-12 w-full sm:w-auto`} onClick={() => setPricingEditorOpen(true)}>
                      {t('backoffice.delivery_pricing_open_editor', 'Edit delivery pricing')}
                    </button>
                  ) : (
                    <p className="text-sm text-slate-600">{t('backoffice.delivery_pricing_readonly')}</p>
                  )}
                  {canEditPublicContact ? (
                    <Link
                      to="/workspace/site-content#public-contact"
                      className={`${btnSecondaryClass} w-full justify-center sm:w-auto`}
                    >
                      {t('backoffice.delivery_pricing_store_edit_public_contact')}
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : null}

            {pricingEditorOpen ? (
              <div className="workspace-panel relative space-y-3">
                <div className="sticky top-0 z-20 -mx-6 mb-2 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/90 bg-white/95 px-6 py-4 backdrop-blur-md sm:-mx-8 sm:px-8">
                  <p className="text-sm font-semibold text-slate-800">
                    <span className="mr-2 inline-flex rounded-md bg-brand-50 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-brand-700">
                      {t('backoffice.delivery_pricing_editor_mode', 'Editing')}
                    </span>
                    {t('backoffice.delivery_pricing_editor_banner', 'Delivery pricing & rates')}
                  </p>
                  <button type="button" className={btnGhostClass} onClick={closePricingEditor}>
                    {t('backoffice.delivery_pricing_back_overview', 'Back to overview')}
                  </button>
                </div>

                {error ? (
                  <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
                ) : null}
                {saved ? (
                  <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">
                    {t('backoffice.delivery_pricing_saved')}
                  </p>
                ) : null}

                <section className={`${sectionCardClass} bg-gradient-to-br from-slate-50/90 to-white`}>
                  <h2 className={sectionTitleClass}>{t('backoffice.delivery_pricing_store_section')}</h2>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    <p>
                      <span className="font-semibold text-slate-900">{t('backoffice.delivery_pricing_store_resolved')}:</span>{' '}
                      {storeEffective ? `${storeEffective.latitude.toFixed(6)}, ${storeEffective.longitude.toFixed(6)}` : '—'}
                      <span className="ml-2 inline-flex rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                        {sourceLabel}
                      </span>
                    </p>
                    <p>
                      <span className="font-semibold text-slate-900">Origin country:</span> United States
                    </p>
                    <p>
                      <span className="font-semibold text-slate-900">Origin address:</span>{' '}
                      <span className="break-words">{storeEffective?.label?.trim() || '—'}</span>
                    </p>
                    <p>
                      <span className="font-semibold text-slate-900">Public contact address:</span>{' '}
                      <span className="break-words">{publicContactAddress?.trim() || '—'}</span>
                    </p>
                  </div>
                  {canEditPublicContact ? (
                    <Link
                      to="/workspace/site-content#public-contact"
                      className="mt-3 inline-flex text-sm font-semibold text-brand-600 hover:text-brand-700 hover:underline"
                    >
                      {t('backoffice.delivery_pricing_store_edit_public_contact')}
                    </Link>
                  ) : null}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button type="button" onClick={() => setShowOverride((v) => !v)} className={btnSecondaryClass}>
                      {t('backoffice.delivery_pricing_store_override_section')}
                    </button>
                    <button
                      type="button"
                      disabled={!canEdit || savingOverride}
                      onClick={() => void clearOverride()}
                      className={btnSecondaryClass}
                    >
                      {t('backoffice.delivery_pricing_store_clear_override')}
                    </button>
                  </div>
                  {showOverride ? (
                    <div className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-200/80 pt-5 sm:grid-cols-3">
                      <div>
                        <label className={fieldLabelClass}>{t('backoffice.delivery_pricing_latitude')}</label>
                        <input
                          className={inputClass}
                          value={overrideLat}
                          onChange={(e) => setOverrideLat(e.target.value)}
                          disabled={!canEdit || savingOverride}
                        />
                      </div>
                      <div>
                        <label className={fieldLabelClass}>{t('backoffice.delivery_pricing_longitude')}</label>
                        <input
                          className={inputClass}
                          value={overrideLng}
                          onChange={(e) => setOverrideLng(e.target.value)}
                          disabled={!canEdit || savingOverride}
                        />
                      </div>
                      <div>
                        <label className={fieldLabelClass}>{t('backoffice.delivery_pricing_label')}</label>
                        <input
                          className={inputClass}
                          value={overrideLabel}
                          onChange={(e) => setOverrideLabel(e.target.value)}
                          disabled={!canEdit || savingOverride}
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <button
                          type="button"
                          disabled={!canEdit || savingOverride}
                          onClick={() => void saveOverride()}
                          className={btnPrimaryClass}
                        >
                          {t('backoffice.delivery_pricing_store_set_override')}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </section>

                <section className={sectionCardClass}>
                  <h2 className={`${sectionTitleClass} mb-4`}>{t('backoffice.delivery_pricing_rules_section')}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={!!pricing.expressEnabled}
                    onChange={(e) => setPricing((p) => (p ? { ...p, expressEnabled: e.target.checked } : p))}
                    disabled={!canEdit || saving}
                  />
                  {t('backoffice.delivery_pricing_express_enabled')}
                </label>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">
                    {t('backoffice.delivery_pricing_store_country')}
                  </label>
                  <Select
                    isDisabled
                    value={selectedStoreCountry}
                    options={countryOptions}
                    classNamePrefix="rs"
                    placeholder="United States"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: 40,
                        borderRadius: 12,
                        borderColor: '#e2e8f0',
                        boxShadow: 'none',
                      }),
                    }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">
                    {t('backoffice.delivery_pricing_days_standard')}
                  </label>
                  <input
                    className={inputClass}
                    inputMode="numeric"
                    value={valueFromDraft('global.standardDays', pricing.standardDays)}
                    onChange={(e) => setDraftValue('global.standardDays', e.target.value)}
                    onBlur={(e) => {
                      const next = Math.max(0, Math.round(toNum(e.target.value)));
                      setPricing((p) => (p ? { ...p, standardDays: next } : p));
                      clearDraftValue('global.standardDays');
                    }}
                    disabled={!canEdit || saving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">
                    {t('backoffice.delivery_pricing_days_express')}
                  </label>
                  <input
                    className={inputClass}
                    inputMode="numeric"
                    value={valueFromDraft('global.expressDays', pricing.expressDays)}
                    onChange={(e) => setDraftValue('global.expressDays', e.target.value)}
                    onBlur={(e) => {
                      const next = Math.max(0, Math.round(toNum(e.target.value)));
                      setPricing((p) => (p ? { ...p, expressDays: next } : p));
                      clearDraftValue('global.expressDays');
                    }}
                    disabled={!canEdit || saving}
                  />
                </div>
              </div>
            </section>

                <section className={sectionCardClass}>
                  <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
                    <h2 className={sectionTitleClass}>{t('backoffice.delivery_pricing_countries_section')}</h2>
                    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                      <div className="min-w-[min(100%,16rem)] flex-1 sm:min-w-[240px]">
                        <Select
                          isDisabled={!canEdit || saving}
                          value={null}
                          options={countryOptions}
                          onChange={(opt) => {
                            if (!opt) return;
                            addCountryByCode((opt as CountryOption).value);
                          }}
                          classNamePrefix="rs"
                          placeholder="Add country…"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: 40,
                              borderRadius: 12,
                              borderColor: '#e2e8f0',
                              boxShadow: 'none',
                            }),
                          }}
                        />
                      </div>
                      <button type="button" disabled={!canEdit || saving} onClick={addCountry} className={btnSecondaryClass}>
                        {t('backoffice.delivery_pricing_add_country')}
                      </button>
                    </div>
                  </div>

              <div className="space-y-4">
                <div className="mb-3">
                  <input
                    className={inputClass}
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder="Search countries…"
                  />
                </div>

                {filteredCountries.map((c) => (
                  <div
                    key={c.countryCode}
                    className={[
                      'relative rounded-2xl border p-4 transition-shadow',
                      expandedCountries[normalizeCountryCode(c.countryCode)]
                        ? 'border-brand-200 bg-brand-50/20 shadow-md ring-2 ring-brand-100'
                        : 'border-slate-200 bg-white hover:border-slate-300',
                    ].join(' ')}
                  >
                    <button
                      type="button"
                      disabled={
                        !canEdit ||
                        saving ||
                        normalizeCountryCode(c.countryCode) === normalizeCountryCode(pricing.storeCountry) ||
                        normalizeCountryCode(c.countryCode) === 'US'
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCountry(c.countryCode);
                      }}
                      className="absolute right-3 top-3 inline-flex items-center justify-center rounded-lg border border-red-200 bg-white p-2 text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-50"
                      title={
                        normalizeCountryCode(c.countryCode) === 'US'
                          ? 'United States cannot be removed.'
                          : normalizeCountryCode(c.countryCode) === normalizeCountryCode(pricing.storeCountry)
                            ? 'You cannot remove the store origin country.'
                            : 'Remove country'
                      }
                      aria-label="Remove country"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(c.countryCode)}
                      className="mb-4 flex w-full flex-wrap items-center justify-between gap-2 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-28">
                          <label className="mb-1 block text-xs font-medium text-slate-700">{t('backoffice.delivery_pricing_country_code')}</label>
                          <div className="text-sm font-semibold text-slate-900">{normalizeCountryCode(c.countryCode)}</div>
                          <div className="text-xs text-slate-600">{countryNameForCode(c.countryCode)}</div>
                        </div>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" onClick={(e) => e.stopPropagation()}>
                              <div className="min-w-[140px]">
                                <label className="mb-1 block text-[11px] font-medium text-slate-600">
                                  Standard ETA (days)
                                </label>
                                <input
                                  className={inputClass}
                                  inputMode="numeric"
                                  value={valueFromDraft(
                                    `country.${c.countryCode}.eta.standard`,
                                    c.standardDays ?? pricing.standardDays,
                                  )}
                                  onChange={(e) =>
                                    setDraftValue(`country.${c.countryCode}.eta.standard`, e.target.value)
                                  }
                                  onBlur={(e) => {
                                    const next = Math.max(0, Math.round(toNum(e.target.value)));
                                    setCountry(c.countryCode, (x) => ({ ...x, standardDays: next }));
                                    clearDraftValue(`country.${c.countryCode}.eta.standard`);
                                  }}
                                  disabled={!canEdit || saving}
                                />
                              </div>
                              <div className="min-w-[140px]">
                                <label className="mb-1 block text-[11px] font-medium text-slate-600">
                                  Express ETA (days)
                                </label>
                                <input
                                  className={inputClass}
                                  inputMode="numeric"
                                  value={valueFromDraft(
                                    `country.${c.countryCode}.eta.express`,
                                    c.expressDays ?? pricing.expressDays,
                                  )}
                                  onChange={(e) =>
                                    setDraftValue(`country.${c.countryCode}.eta.express`, e.target.value)
                                  }
                                  onBlur={(e) => {
                                    const next = Math.max(0, Math.round(toNum(e.target.value)));
                                    setCountry(c.countryCode, (x) => ({ ...x, expressDays: next }));
                                    clearDraftValue(`country.${c.countryCode}.eta.express`);
                                  }}
                                  disabled={!canEdit || saving}
                                />
                              </div>
                            </div>
                        <label className="flex items-center gap-2 text-sm text-slate-800" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={c.enabled !== false}
                            onChange={(e) => setCountry(c.countryCode, (x) => ({ ...x, enabled: e.target.checked }))}
                            disabled={!canEdit || saving}
                          />
                          Delivery enabled
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-800">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={!!c.express.enabled}
                            onChange={(e) => setCountry(c.countryCode, (x) => ({ ...x, express: { ...x.express, enabled: e.target.checked } }))}
                            disabled={!canEdit || saving}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {t('backoffice.delivery_pricing_country_express_enabled')}
                        </label>
                      </div>
                      <ChevronDown
                        className={[
                          'h-5 w-5 shrink-0 text-slate-500 transition-transform',
                          expandedCountries[normalizeCountryCode(c.countryCode)] ? 'rotate-180' : '',
                        ].join(' ')}
                        aria-hidden
                      />
                    </button>

                    {expandedCountries[normalizeCountryCode(c.countryCode)] ? (
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {t('storefront.delivery_speed_standard')}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_border_fee')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(
                                `country.${c.countryCode}.standard.borderFeeUsd`,
                                c.standard.borderFeeUsd,
                              )}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.standard.borderFeeUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  standard: { ...x.standard, borderFeeUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.standard.borderFeeUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_base_fee')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(
                                `country.${c.countryCode}.standard.baseFeeUsd`,
                                c.standard.baseFeeUsd ?? 0,
                              )}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.standard.baseFeeUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  standard: { ...x.standard, baseFeeUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.standard.baseFeeUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_per_mile')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(
                                `country.${c.countryCode}.standard.perMileUsd`,
                                c.standard.perMileUsd,
                              )}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.standard.perMileUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  standard: { ...x.standard, perMileUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.standard.perMileUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_per_lb')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(`country.${c.countryCode}.standard.perLbUsd`, c.standard.perLbUsd)}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.standard.perLbUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  standard: { ...x.standard, perLbUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.standard.perLbUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                        </div>

                        {normalizeCountryCode(c.countryCode) === 'US' ? (
                          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                              {t('backoffice.delivery_pricing_us_threshold_section')}
                            </div>
                            <label className="mb-2 flex items-center gap-2 text-sm text-slate-800">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={!!c.standard.freeThreshold}
                                onChange={(e) =>
                                  setCountry(c.countryCode, (x) => ({
                                    ...x,
                                    standard: {
                                      ...x.standard,
                                      freeThreshold: e.target.checked
                                        ? {
                                            minSubtotalUsd: 0,
                                            maxWeightLbs: 0,
                                            feeUsd: 0,
                                          }
                                        : undefined,
                                    },
                                  }))
                                }
                                disabled={!canEdit || saving}
                              />
                              Enable threshold
                            </label>
                            {c.standard.freeThreshold ? (
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">
                                    {t('backoffice.delivery_pricing_free_min_subtotal')}
                                  </label>
                                  <input
                                    className={inputClass}
                                    inputMode="decimal"
                                    value={valueFromDraft(
                                      `country.${c.countryCode}.standard.free.minSubtotalUsd`,
                                      c.standard.freeThreshold.minSubtotalUsd,
                                    )}
                                    onChange={(e) =>
                                      setDraftValue(
                                        `country.${c.countryCode}.standard.free.minSubtotalUsd`,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) => {
                                      const next = clamp0(toNum(e.target.value));
                                      setCountry(c.countryCode, (x) => ({
                                        ...x,
                                        standard: {
                                          ...x.standard,
                                          freeThreshold: {
                                            ...(x.standard.freeThreshold as FreeThreshold),
                                            minSubtotalUsd: next,
                                          },
                                        },
                                      }));
                                      clearDraftValue(`country.${c.countryCode}.standard.free.minSubtotalUsd`);
                                    }}
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">
                                    {t('backoffice.delivery_pricing_free_max_weight')}
                                  </label>
                                  <input
                                    className={inputClass}
                                    inputMode="decimal"
                                    value={valueFromDraft(
                                      `country.${c.countryCode}.standard.free.maxWeightLbs`,
                                      c.standard.freeThreshold.maxWeightLbs,
                                    )}
                                    onChange={(e) =>
                                      setDraftValue(
                                        `country.${c.countryCode}.standard.free.maxWeightLbs`,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) => {
                                      const next = clamp0(toNum(e.target.value));
                                      setCountry(c.countryCode, (x) => ({
                                        ...x,
                                        standard: {
                                          ...x.standard,
                                          freeThreshold: {
                                            ...(x.standard.freeThreshold as FreeThreshold),
                                            maxWeightLbs: next,
                                          },
                                        },
                                      }));
                                      clearDraftValue(`country.${c.countryCode}.standard.free.maxWeightLbs`);
                                    }}
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">
                                    {t('backoffice.delivery_pricing_free_fee')}
                                  </label>
                                  <input
                                    className={inputClass}
                                    inputMode="decimal"
                                    value={valueFromDraft(
                                      `country.${c.countryCode}.standard.free.feeUsd`,
                                      c.standard.freeThreshold.feeUsd,
                                    )}
                                    onChange={(e) =>
                                      setDraftValue(`country.${c.countryCode}.standard.free.feeUsd`, e.target.value)
                                    }
                                    onBlur={(e) => {
                                      const next = clamp0(toNum(e.target.value));
                                      setCountry(c.countryCode, (x) => ({
                                        ...x,
                                        standard: {
                                          ...x.standard,
                                          freeThreshold: {
                                            ...(x.standard.freeThreshold as FreeThreshold),
                                            feeUsd: next,
                                          },
                                        },
                                      }));
                                      clearDraftValue(`country.${c.countryCode}.standard.free.feeUsd`);
                                    }}
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                          {t('storefront.delivery_speed_express')}
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_border_fee')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(
                                `country.${c.countryCode}.express.borderFeeUsd`,
                                c.express.borderFeeUsd,
                              )}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.express.borderFeeUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  express: { ...x.express, borderFeeUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.express.borderFeeUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_base_fee')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(
                                `country.${c.countryCode}.express.baseFeeUsd`,
                                c.express.baseFeeUsd ?? 0,
                              )}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.express.baseFeeUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  express: { ...x.express, baseFeeUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.express.baseFeeUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_per_mile')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(
                                `country.${c.countryCode}.express.perMileUsd`,
                                c.express.perMileUsd,
                              )}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.express.perMileUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  express: { ...x.express, perMileUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.express.perMileUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-slate-700">
                              {t('backoffice.delivery_pricing_per_lb')}
                            </label>
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={valueFromDraft(`country.${c.countryCode}.express.perLbUsd`, c.express.perLbUsd)}
                              onChange={(e) =>
                                setDraftValue(`country.${c.countryCode}.express.perLbUsd`, e.target.value)
                              }
                              onBlur={(e) => {
                                const next = clamp0(toNum(e.target.value));
                                setCountry(c.countryCode, (x) => ({
                                  ...x,
                                  express: { ...x.express, perLbUsd: next },
                                }));
                                clearDraftValue(`country.${c.countryCode}.express.perLbUsd`);
                              }}
                              disabled={!canEdit || saving}
                            />
                          </div>
                        </div>

                        {normalizeCountryCode(c.countryCode) === 'US' ? (
                          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                              {t('backoffice.delivery_pricing_us_threshold_section')}
                            </div>
                            <label className="mb-2 flex items-center gap-2 text-sm text-slate-800">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={!!c.express.freeThreshold}
                                onChange={(e) =>
                                  setCountry(c.countryCode, (x) => ({
                                    ...x,
                                    express: {
                                      ...x.express,
                                      freeThreshold: e.target.checked
                                        ? {
                                            minSubtotalUsd: 0,
                                            maxWeightLbs: 0,
                                            feeUsd: 0,
                                          }
                                        : undefined,
                                    },
                                  }))
                                }
                                disabled={!canEdit || saving}
                              />
                              Enable threshold
                            </label>
                            {c.express.freeThreshold ? (
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">
                                    {t('backoffice.delivery_pricing_free_min_subtotal')}
                                  </label>
                                  <input
                                    className={inputClass}
                                    inputMode="decimal"
                                    value={valueFromDraft(
                                      `country.${c.countryCode}.express.free.minSubtotalUsd`,
                                      c.express.freeThreshold.minSubtotalUsd,
                                    )}
                                    onChange={(e) =>
                                      setDraftValue(
                                        `country.${c.countryCode}.express.free.minSubtotalUsd`,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) => {
                                      const next = clamp0(toNum(e.target.value));
                                      setCountry(c.countryCode, (x) => ({
                                        ...x,
                                        express: {
                                          ...x.express,
                                          freeThreshold: {
                                            ...(x.express.freeThreshold as FreeThreshold),
                                            minSubtotalUsd: next,
                                          },
                                        },
                                      }));
                                      clearDraftValue(`country.${c.countryCode}.express.free.minSubtotalUsd`);
                                    }}
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">
                                    {t('backoffice.delivery_pricing_free_max_weight')}
                                  </label>
                                  <input
                                    className={inputClass}
                                    inputMode="decimal"
                                    value={valueFromDraft(
                                      `country.${c.countryCode}.express.free.maxWeightLbs`,
                                      c.express.freeThreshold.maxWeightLbs,
                                    )}
                                    onChange={(e) =>
                                      setDraftValue(
                                        `country.${c.countryCode}.express.free.maxWeightLbs`,
                                        e.target.value,
                                      )
                                    }
                                    onBlur={(e) => {
                                      const next = clamp0(toNum(e.target.value));
                                      setCountry(c.countryCode, (x) => ({
                                        ...x,
                                        express: {
                                          ...x.express,
                                          freeThreshold: {
                                            ...(x.express.freeThreshold as FreeThreshold),
                                            maxWeightLbs: next,
                                          },
                                        },
                                      }));
                                      clearDraftValue(`country.${c.countryCode}.express.free.maxWeightLbs`);
                                    }}
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium text-slate-700">
                                    {t('backoffice.delivery_pricing_free_fee')}
                                  </label>
                                  <input
                                    className={inputClass}
                                    inputMode="decimal"
                                    value={valueFromDraft(
                                      `country.${c.countryCode}.express.free.feeUsd`,
                                      c.express.freeThreshold.feeUsd,
                                    )}
                                    onChange={(e) =>
                                      setDraftValue(`country.${c.countryCode}.express.free.feeUsd`, e.target.value)
                                    }
                                    onBlur={(e) => {
                                      const next = clamp0(toNum(e.target.value));
                                      setCountry(c.countryCode, (x) => ({
                                        ...x,
                                        express: {
                                          ...x.express,
                                          freeThreshold: {
                                            ...(x.express.freeThreshold as FreeThreshold),
                                            feeUsd: next,
                                          },
                                        },
                                      }));
                                      clearDraftValue(`country.${c.countryCode}.express.free.feeUsd`);
                                    }}
                                    disabled={!canEdit || saving}
                                  />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>

                {canEdit ? (
                  <div className="sticky bottom-0 z-10 -mx-6 mt-10 flex flex-wrap items-center justify-end gap-3 border-t border-slate-200/90 bg-white/95 px-6 py-5 backdrop-blur-md sm:-mx-8 sm:px-8">
                    <button type="button" className={btnSecondaryClass} onClick={closePricingEditor}>
                      {t('backoffice.delivery_pricing_back_overview', 'Back to overview')}
                    </button>
                    <button
                      type="button"
                      className={btnPrimaryClass}
                      disabled={saving || !isPricingDirty}
                      onClick={() => void save()}
                    >
                      {saving ? t('common.saving') : t('common.save')}
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">{t('backoffice.delivery_pricing_readonly')}</p>
                )}
              </div>
            ) : null}
          </>
        )}
      </main>
    </MainLayout>
  );
}

